import React, { useMemo } from 'react';
import { VehicleDescriptionItemWithCategory, VehicleDescriptionItem } from 'types';
import { Checkbox, List, ListItem, Text } from '@groupelacentrale/contact-storybook';
import {
  VehicleDescriptionFormViewSection,
  VehicleDescriptionFragmentProps,
} from './VehicleDescriptionFormViewSection';
import { VehicleDescriptionFormViewSectionProps as Props } from './VehicleDescriptionFormViewSectionBasic';

interface VehicleDescriptionSectionWithCategoryProps extends Props<VehicleDescriptionItemWithCategory> {
  translateCategoryName: (name: string) => string;
}

export type VehicleDescriptionFamily = { label: string; vehicleDescriptionList: VehicleDescriptionItem[] };
export type VehicleDescriptionFamilyLabels = { label: string; options: string[] };

const ListWithCategory: React.FC<VehicleDescriptionFragmentProps> = ({
  name,
  checkedElements,
  handleCheck,
  handleKeyPressCheck,
}) => {
  return (
    <>
      {(checkedElements as VehicleDescriptionFamily[]).map(family => (
        <React.Fragment key={family.label}>
          <Text weight="bold" className="vehicle-family-name">
            {family.label}
          </Text>
          <List>
            {family.vehicleDescriptionList.map(vehicleDescription => (
              <ListItem
                key={'li' + vehicleDescription.label}
                tabIndex={0}
                onKeyPress={handleKeyPressCheck}
                data-option={vehicleDescription.label}
              >
                <Checkbox
                  name={name}
                  label={vehicleDescription.label}
                  value={vehicleDescription.label}
                  checked={vehicleDescription.selected}
                  onChange={handleCheck}
                />
              </ListItem>
            ))}
          </List>
        </React.Fragment>
      ))}
    </>
  );
};
export const VehicleDescriptionSectionWithCategory: React.FC<VehicleDescriptionSectionWithCategoryProps> = ({
  vehicleDescriptionList,
  translateCategoryName,
  ...props
}) => {
  // Seperate items into 2 lists : checkedElements, uncheckedElements
  const [checkedElements, uncheckedElements] = useMemo(
    () =>
      vehicleDescriptionList.reduce<(VehicleDescriptionFamily | VehicleDescriptionFamilyLabels)[][]>(
        ([checked, unchecked], vehicleDescriptionItem) => {
          const categoryName = translateCategoryName(vehicleDescriptionItem.category ?? '');
          if (vehicleDescriptionItem.selected) {
            for (const category of checked as VehicleDescriptionFamily[]) {
              if (category.label === categoryName) {
                category.vehicleDescriptionList.push(vehicleDescriptionItem);
                return [checked, unchecked];
              }
            }
            checked.push({ label: categoryName, vehicleDescriptionList: [vehicleDescriptionItem] });
            checked.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
            return [checked, unchecked];
          } else {
            for (const category of unchecked as VehicleDescriptionFamilyLabels[]) {
              if (category.label === categoryName) {
                category.options.push(vehicleDescriptionItem.label);
                return [checked, unchecked];
              }
            }
            unchecked.push({ label: categoryName, options: [vehicleDescriptionItem.label] });
            unchecked.sort((a, b) => (a.label > b.label ? 1 : b.label > a.label ? -1 : 0));
            return [checked, unchecked];
          }
        },
        [[], []],
      ),
    [vehicleDescriptionList, translateCategoryName],
  );

  const uncheckedCounter = useMemo(
    () =>
      (uncheckedElements as VehicleDescriptionFamilyLabels[]).reduce(
        (count, element) => count + element.options.length,
        0,
      ),
    [uncheckedElements],
  );

  return (
    <VehicleDescriptionFormViewSection
      {...props}
      vehicleDescriptionList={vehicleDescriptionList}
      checkedElements={checkedElements as VehicleDescriptionFamily[]}
      uncheckedElements={uncheckedElements as VehicleDescriptionFamilyLabels[]}
      uncheckedCounter={uncheckedCounter}
      Fragment={ListWithCategory}
    />
  );
};
